import {
	getColorScale,
	getPercentageFromContinentsObj,
	getPercentageFromCountriesVals,
	getProjection,
	getValuesFromCountriesByContintents,
} from './helpers';

import { scaleLinear } from 'd3-scale';
import { max, easeLinear, min, interpolate, selection, zoom, select } from 'd3';
import {
	MIN_SCALE_VALUE,
	MIN_VALUE,
	WORLD_CONTINENTS,
	WORLD_COUNTRIES,
} from './constants';
import { useRef, useEffect, useState } from '@wordpress/element';

import cartogram from './libs/cartogram';

const SVGGraph = (props) => {
	const {
		unit,
		data,
		colors,
		maxDistorsion: adminMaxDistorsion,
		projection: propsProjection,
		strokeColorStrength,
	} = props;
	const [transitionStatus, setTransitionStatus] = useState(0);
	const [tooltipData, setTooltipData] = useState();

	const [k, setK] = useState(1);
	const [x, setX] = useState(0);
	const [y, setY] = useState(0);
	const [dimensions, setDimensions] = useState({
		width: 960,
		height: 500,
	});

	const svgRef = useRef(null);

	let percentages;
	let topology;
	let geometries;
	if ('country' === unit) {
		percentages = getPercentageFromCountriesVals(data);

		topology = WORLD_COUNTRIES;

		geometries = WORLD_COUNTRIES.objects.countries.geometries;
	} else {
		const continentsValues = getValuesFromCountriesByContintents(data);
		percentages = getPercentageFromContinentsObj(continentsValues);
		topology = WORLD_CONTINENTS;

		geometries = WORLD_CONTINENTS.objects.continent.geometries;
	}

	const values = Object.values(percentages);

	const maxVal = max(values);

	const colorScale = getColorScale(colors, maxVal);

	// invert value so the bigger the distorsion number the bigger the distortion
	// creates a ceil of 1000
	const maxDistorsion = adminMaxDistorsion * 10 * transitionStatus;

	const scale = scaleLinear()
		.domain([MIN_VALUE, maxVal])

		.range([MIN_SCALE_VALUE, maxDistorsion]);

	const getCountryValue = (d) => percentages[d.properties.NAME] || MIN_VALUE;

	const projection = getProjection(propsProjection)
		.scale(dimensions.height / Math.PI)
		.translate([dimensions.width / 2, dimensions.height / 2]);

	const carto = cartogram()
		.projection(projection)
		.value((d) => {
			return scale(getCountryValue(d));
		});

	const features = carto(topology, geometries).features;

	useEffect(() => {
		const { width, height } = select(svgRef.current).node().getBBox();

		setDimensions({ width, height });
	}, [window.innerWidth]);

	// Ref: https://reactfordataviz.com/articles/how-to-drive-react-state-with-d3-transitions-for-complex-animation/
	useEffect(() => {
		// Creates transition as in d3
		selection()
			.transition('map-transform')
			.duration(1500)
			.ease(easeLinear)
			.tween('transitionStatus', () => {
				const percentInterpolate = interpolate(0, 1);

				return (t) => setTransitionStatus(percentInterpolate(t));
			});
	}, [adminMaxDistorsion]);

	useEffect(() => {
		// Now we can work on setting the zoom translate extent:
		// Zooomed projected limits of screen in lat long:
		const invertZeroPoint = projection.invert([0, 0]);
		const left = invertZeroPoint[0];
		const top = invertZeroPoint[1];

		const invertFullPoint = projection.invert([
			dimensions.width,
			dimensions.height,
		]);
		const right = invertFullPoint[0];
		const bottom = invertFullPoint[1];

		// Projected limits of screen in projected unzoomed coordinates:
		const topLeft = projection([left, top]);
		const bottomRight = projection([right, bottom]);

		// Set the zoom translate extent:
		const zoomObj = zoom()
			.translateExtent([topLeft, bottomRight])
			.scaleExtent([1, 8])
			.on('zoom', (event) => {
				const { x: eX, y: eY, k: eK } = event.transform;
				setK(eK > 0 ? eK : 1);
				setX(eX);
				setY(eY);
			});
		select(svgRef.current).call(zoomObj);
	}, [dimensions]);

	const onMouseEnter = (e, feature) => {
		const val = getCountryValue(feature);

		if (
			!tooltipData ||
			tooltipData.feature.properties.NAME !== feature.properties.NAME
		) {
			const centroid = carto.path.centroid(feature);

			setTooltipData({
				feature,
				text: `${feature.properties.NAME}: ${Math.round(val * 100)}%`,
				centroid,
			});
		}
	};
	const onMouseLeave = (feature) => {
		if (
			!feature ||
			(tooltipData &&
				tooltipData.feature.properties.NAME !== feature.properties.NAME)
		) {
			setTooltipData();
		}
	};

	return (
		<>
			<svg
				id="map"
				preserveAspectRatio="xMidYMid meet"
				viewBox={[0, 0, dimensions.width, dimensions.height]}
				ref={svgRef}
			>
				<g id="countries" transform={`translate(${x},${y})scale(${k})`}>
					{features.map((f) => {
						const val = getCountryValue(f);

						const stroke = colorScale(strokeColorStrength / 100);
						return (
							<Path
								feature={f}
								key={f.properties.NAME}
								targetPath={carto.path(f)}
								color={colorScale(val)}
								stroke={stroke}
								onMouseEnter={onMouseEnter}
								onMouseLeave={onMouseLeave}
							/>
						);
					})}
					{tooltipData && (
						<text
							className="map-tooltip"
							transform={`translate(${tooltipData.centroid[0]},${tooltipData.centroid[1]})`}
							fontSize={16 - k}
						>
							{tooltipData.text}
						</text>
					)}
				</g>
			</svg>
		</>
	);
};

const Path = ({
	feature,
	targetPath,
	color,
	stroke,
	onMouseEnter,
	onMouseLeave,
}) => {
	return (
		<path
			className="country"
			fill={color}
			d={targetPath}
			stroke={stroke}
			onMouseEnter={(e) => onMouseEnter(e, feature)}
			onMouseLeave={() => onMouseLeave(feature)}
		/>
	);
};

export default SVGGraph;
